/**
 * "Move to another business unit" for one device, in the Edge Fleet drawer
 * (founder decision, 22 September 2026).
 *
 * Moving a laptop between units is a deliberate console action: installing
 * another unit's package never moves one. When a laptop moves, the findings
 * already found on it move too, so the confirm step says plainly who will see
 * them afterwards and who will not.
 *
 * Whether THIS operator may move the device, and to which units, comes from
 * the server (GET /v1/edge/fleet/:id/move-business-unit), judged by the same
 * rules as the move itself: the edge fleet write permission and authority over
 * both the device's unit and the target. Without them the control is shown
 * disabled with the reason, never offered and then refused.
 */
import { useEffect, useState } from 'react';
import { ArrowRightLeft, Building2, Loader2 } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  getEdgePodBusinessUnitMoveOptions,
  moveEdgePodBusinessUnit,
  type EdgePodBusinessUnitMoveOptions,
  type EdgePodDetail,
} from '@/lib/api';

interface Props {
  pod: EdgePodDetail;
  onMoved: (pod: EdgePodDetail) => void;
}

/**
 * What the move does to who sees the device's findings, in plain words. The
 * confirm step shows exactly this; exported so the test can pin it.
 */
export function moveConsequence(device: string, from: string | null, to: string): string {
  const who =
    from === null
      ? `will become visible to ${to} only. Today this device has no business unit, so every unit can see them; after the move, other units will not.`
      : `will become visible to ${to} and hidden from ${from}.`;
  return (
    `The findings already found on ${device} ${who} ` +
    `Its future scans will report to ${to}. Its installed package is not changed, ` +
    `and installing another unit's package later will not move it back.`
  );
}

export function EdgePodBusinessUnitMove({ pod, onMoved }: Props): JSX.Element {
  const [options, setOptions] = useState<EdgePodBusinessUnitMoveOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState<'idle' | 'choose' | 'confirm'>('idle');
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setOptions(null);
    setLoadError(null);
    setStep('idle');
    getEdgePodBusinessUnitMoveOptions(pod.id)
      .then((o) => {
        if (!cancelled) setOptions(o);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'could not load the business units');
      });
    return () => {
      cancelled = true;
    };
  }, [pod.id, pod.businessUnitId, pod.decommissionedAt]);

  const currentName =
    options?.current?.name ??
    (pod.businessUnitId ? pod.businessUnitId : 'None (every business unit can see this device)');
  const target = options?.targets.find((t) => t.id === targetId) ?? null;
  const reasonOk = reason.trim().length >= 3;

  function start(): void {
    setError(null);
    setDone(null);
    setTargetId(options?.targets[0]?.id ?? '');
    setReason('');
    setStep('choose');
  }

  async function confirm(): Promise<void> {
    if (!target || !reasonOk) return;
    setBusy(true);
    setError(null);
    try {
      const r = await moveEdgePodBusinessUnit(pod.id, target.id, reason.trim());
      setStep('idle');
      setDone(
        `Moved to ${target.name}. ${r.move.findingsMoved} finding${r.move.findingsMoved === 1 ? '' : 's'} ` +
          `on ${r.move.assetsMoved} file${r.move.assetsMoved === 1 ? '' : 's'} moved with it.`,
      );
      onMoved(r.pod);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'the move failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <GlassCard className="p-3">
      <CardHeader className="p-0 mb-2">
        <CardTitle className="text-sm inline-flex items-center gap-1.5">
          <Building2 className="h-4 w-4" /> Business unit
        </CardTitle>
        <CardDescription clamp className="text-[11px]">
          Decides which unit&apos;s people see this device and what it finds. Only a move made here
          changes it; installing another unit&apos;s package does not.
        </CardDescription>
      </CardHeader>

      <div className="text-xs mb-2">
        <span className="text-muted-foreground">Current: </span>
        <span className="font-medium">{currentName}</span>
      </div>

      {!options && !loadError && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking what you can do…
        </div>
      )}
      {loadError && <div className="text-[11px] text-warning">{loadError}</div>}

      {options && step === 'idle' && (
        <div>
          <Button size="sm" variant="glass" disabled={!options.allowed} onClick={start}>
            <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />
            Move to another business unit
          </Button>
          {!options.allowed && options.reason && (
            <div className="text-[11px] text-muted-foreground mt-1">{options.reason}</div>
          )}
        </div>
      )}

      {options && step === 'choose' && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor={`bu-move-target-${pod.id}`} className="text-xs text-muted-foreground block mb-0.5">
                Move to
              </label>
              <select
                id={`bu-move-target-${pod.id}`}
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {options.targets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`bu-move-reason-${pod.id}`} className="text-xs text-muted-foreground block mb-0.5">
                Reason (required)
              </label>
              <input
                id={`bu-move-reason-${pod.id}`}
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="laptop handed to another team"
                maxLength={250}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="glass" onClick={() => setStep('idle')}>
              Cancel
            </Button>
            <Button size="sm" disabled={!target || !reasonOk} onClick={() => setStep('confirm')}>
              Review the move
            </Button>
          </div>
        </div>
      )}

      {options && step === 'confirm' && target && (
        <div className="space-y-2">
          <div className="glass-lite border border-warning/40 rounded-md p-2 text-xs" role="alert">
            <div className="font-medium mb-1">
              Move {pod.displayName} from {options.current?.name ?? 'no business unit'} to {target.name}?
            </div>
            <div className="text-muted-foreground">
              {moveConsequence(pod.displayName, options.current?.name ?? null, target.name)}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="glass" disabled={busy} onClick={() => setStep('choose')}>
              Back
            </Button>
            <Button
              data-action="disc.adm_edge_fleet.a21"
              size="sm"
              variant="destructive"
              disabled={busy}
              onClick={() => void confirm()}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              Move it to {target.name}
            </Button>
          </div>
        </div>
      )}

      {error && <div className="text-[11px] text-warning mt-2">{error}</div>}
      {done && <div className="text-[11px] text-success mt-2">{done}</div>}
    </GlassCard>
  );
}
